/**
 * ProductionEditorCanvas — ReactFlow canvas wired to the
 * ProductionEditorProvider. Handles drop-from-sidebar to create nodes.
 *
 * Use inside <ProductionEditorProvider>.
 */
import { useCallback, type DragEvent, type ReactNode } from 'react';
import {
    Background,
    Controls,
    ReactFlow,
    useReactFlow,
    type Node,
    type NodeTypes,
    type EdgeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './production-editor.css';
import { useProductionEditor } from './ProductionEditorProvider';

interface ProductionEditorCanvasProps {
    /**
     * Builds the new node when an item is dropped from the sidebar.
     * Default: a 'default' node labelled with the dragged item's label.
     */
    nodeFactory?: (
        params: { id: string; position: { x: number; y: number }; draggedType: string; label: string; data?: Record<string, unknown> },
    ) => Node;
    /** Custom node renderers (Nemo-style cards, group nodes, etc). */
    nodeTypes?: NodeTypes;
    /** Custom edge renderers (animated edges, etc). */
    edgeTypes?: EdgeTypes;
    /** Optional className for the container. */
    className?: string;
    /** Min height of the canvas. Default 480. */
    minHeight?: number | string;
    /**
     * Overlay React Flow `<Panel>` children (e.g. sidebars, action bars).
     * Mirrors Nemo's `<Panel position="top-left">` pattern.
     */
    children?: ReactNode;
}

const defaultNodeFactory: NonNullable<ProductionEditorCanvasProps['nodeFactory']> = ({
    id,
    position,
    label,
    data,
}) => ({
    id,
    type: 'default',
    position,
    data: { label, ...(data ?? {}) },
});

export function ProductionEditorCanvas({
    nodeFactory = defaultNodeFactory,
    nodeTypes,
    edgeTypes,
    className,
    minHeight = 480,
    children,
}: ProductionEditorCanvasProps) {
    const {
        nodes,
        edges,
        setNodes,
        onNodesChange,
        onEdgesChange,
        draggedItemRef,
        setSelectedNodes,
    } = useProductionEditor();
    const { screenToFlowPosition } = useReactFlow();

    const onDragOver = useCallback((event: DragEvent) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback(
        (event: DragEvent) => {
            event.preventDefault();
            const item = draggedItemRef.current;
            if (!item) return;

            const position = screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });
            const newNode = nodeFactory({
                id: `${item.type}-${Date.now()}`,
                position,
                draggedType: item.type,
                label: item.label,
                data: item.data,
            });
            setNodes((prev) => [...prev, newNode]);
        },
        [draggedItemRef, screenToFlowPosition, setNodes, nodeFactory],
    );

    // ReactFlow needs an explicit height on its parent — `minHeight` alone
    // collapses to 0 when used with `100%`. We resolve a real height:
    //   - number    -> `${n}px`
    //   - any string -> use it as-is (e.g. '100%', 'calc(...)', '60vh')
    const resolvedHeight =
        typeof minHeight === 'number' ? `${minHeight}px` : minHeight;

    return (
        <div
            className={className}
            style={{
                width: '100%',
                height: resolvedHeight,
                minHeight: resolvedHeight,
                background: 'var(--nj-semantic-color-background-neutral-secondary-default)',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid var(--nj-core-color-grey-200)',
            }}
        >
            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onSelectionChange={({ nodes: sel }) => setSelectedNodes(sel)}
                fitView
                proOptions={{ hideAttribution: true }}
            >
                {children}
                <Background />
                <Controls position="bottom-right" />
            </ReactFlow>
        </div>
    );
}
